// Labels and sentences for the admin page. Dates are formatted by hand: the
// short month from Intl differs between engines ("Sep" / "Sept").

import type { AdminPlayerReach, AdminSeason, AdminSeasonPhase, AdminSeasonStatus, SettleSkip } from "../api/types";
import { readGameError } from "../api/gameApi";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-11-17" -> "Tue 17 Nov 2026". */
export function fmtDay(dayKey: string | null | undefined): string {
  if (!dayKey) return "none";
  const d = new Date(`${dayKey}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return dayKey;
  return `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** An instant in the browser's zone, "17 Nov 2026, 06:50". */
export function fmtMoment(iso: string | null | undefined): string {
  if (!iso) return "none";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const PHASE_LABEL: Record<AdminSeasonPhase, string> = {
  draft: "Draft",
  announced: "Announced",
  open: "Joining open",
  active: "Running",
  ended: "Ended",
  archived: "Archived",
};

export const STATUS_ACTION: Partial<Record<AdminSeasonStatus, string>> = {
  announced: "Announce",
  open: "Open joining",
  active: "Start now",
  ended: "End the season",
  archived: "Archive",
};

/** What each move does, said before Nassos confirms it. Also used before creating a season that is not a draft. */
export function transitionConfirm(
  season: Pick<AdminSeason, "dayKeyFrom" | "dayKeyTo" | "opensAt" | "currentDayKey">,
  to: AdminSeasonStatus,
): {
  title: string;
  message: string;
  confirmLabel: string;
  tone: "default" | "danger";
} {
  const start = fmtDay(season.dayKeyFrom);
  const forward = "A season only moves forward, so this cannot be undone.";
  switch (to) {
    case "announced":
      return {
        title: "Announce the season?",
        message: `/game shows the teaser with the dates and the prize. Joining stays closed until ${
          season.opensAt ? fmtMoment(season.opensAt) : "you open it"
        }. ${forward}`,
        confirmLabel: "Announce",
        tone: "default",
      };
    case "open":
      return {
        title: "Open joining?",
        message: `Anyone signed in can join from /game straight away. The first race is on ${start}. ${forward}`,
        confirmLabel: "Open joining",
        tone: "default",
      };
    case "active":
      return {
        title: "Start the season now?",
        message: `The dates start it on ${start} without you. Starting now switches /game to the standings early; only days from ${start} on score. ${forward}`,
        confirmLabel: "Start now",
        tone: "danger",
      };
    case "ended": {
      // the backend keeps the AOTD day the end happens on and cuts every later one
      const last = season.currentDayKey < season.dayKeyTo ? season.currentDayKey : season.dayKeyTo;
      const cut =
        last < season.dayKeyFrom
          ? "No race has counted yet, so nothing will score."
          : `${fmtDay(last)} is the last day that scores. Its art and guessing can still settle after this; the days after it are never scored and nobody gets a message for them.`;
      return {
        title: "End the season now?",
        message: `Joining closes and /game shows the final results. ${cut} ${forward}`,
        confirmLabel: "End the season",
        tone: "danger",
      };
    }
    case "archived":
      return {
        title: "Archive the season?",
        message:
          "It disappears from /game and the home board, and nobody can join or see the standings. It stays here under past seasons. " +
          forward,
        confirmLabel: "Archive",
        tone: "danger",
      };
    default:
      return { title: `Move the season to ${to}?`, message: forward, confirmLabel: "Move", tone: "default" };
  }
}

export const SKIP_TEXT: Record<SettleSkip, string> = {
  no_season: "The season was not found.",
  not_season: "That game is not a season.",
  phase: "Days settle only while the season is running or after it ended.",
  out_of_range: "That day is outside the season.",
  not_revealed: "That day's art is not out yet, so it cannot be scored.",
};

export function reachText(r: AdminPlayerReach | null): { text: string; tone: "ok" | "off" | "warn" } {
  if (!r) return { text: "unknown", tone: "warn" };
  if (!r.reachable) {
    if (r.push === "ok" && !r.appInstalled) return { text: "tab push only", tone: "warn" };
    if (r.telegram === "muted" || r.telegram === "blocked") return { text: `Telegram ${r.telegram}`, tone: "warn" };
    if (r.push === "lost") return { text: "push lost", tone: "warn" };
    return { text: "none", tone: "off" };
  }
  const parts = r.via.map((v) => (v === "push" ? (r.appInstalled ? "push (app)" : "push") : "Telegram"));
  return { text: parts.join(" + "), tone: "ok" };
}

/** The backend's plain message, or a short fallback. */
export function errorText(err: unknown, fallback: string): string {
  const e = readGameError(err);
  if (e.message) return e.message;
  if (e.status === 403) return "Admins only.";
  if (e.code) return e.code;
  return fallback;
}
